import { type FC, memo, useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  ButtonBase,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  ChevronRight,
  ExpandMore,
  ImageNotSupported,
  Opacity,
} from '@mui/icons-material';

import { type FileContent } from '#apps/client-web/features/file-storage/services/file-storage';

import { type AuditWaterFixtureFormEntry } from '#packages/data-collection/models/audit-water-fixture-form';

const THUMB_SIZE = 72;

export type WaterFixtureSectionGroup = {
  id: string;
  label: string;
  entries: Array<{
    link: string;
    entry: AuditWaterFixtureFormEntry;
    fixtureTypeLabel: string;
    thumbnail: FileContent | null;
  }>;
};

export type AuditFormWaterFixturePageRendererProps = {
  groups: WaterFixtureSectionGroup[];
};

const EntryThumbnail: FC<{ content: FileContent | null }> = ({ content }) => {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!content) {
      setSrc(null);
      return;
    }
    const blob = content instanceof Blob ? content : new Blob([content]);
    const url = URL.createObjectURL(blob);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [content]);

  return (
    <Box
      sx={(theme) => ({
        width: THUMB_SIZE,
        height: THUMB_SIZE,
        flexShrink: 0,
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: theme.palette.action.hover,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mr: 2,
      })}>
      {src ? (
        <Box
          component="img"
          src={src}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <ImageNotSupported
          sx={(theme) => ({ color: theme.palette.action.disabled })}
        />
      )}
    </Box>
  );
};

export const AuditFormWaterFixturePageRenderer: FC<AuditFormWaterFixturePageRendererProps> =
  memo(({ groups }) => (
    <Box flexGrow={1}>
      {groups.map((group) => (
        <Accordion key={group.id} variant="outlined">
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Opacity fontSize="small" />
              <Typography variant="body1">{group.label}</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack>
              {group.entries.map(
                ({ link, entry, fixtureTypeLabel, thumbnail }, index) => (
                  <ButtonBase
                    key={link}
                    component={Link}
                    to={link}
                    sx={{ display: 'block', textAlign: 'start' }}>
                    <Grid container alignItems="center" sx={{ p: 2 }}>
                      <Grid>
                        <EntryThumbnail content={thumbnail} />
                      </Grid>
                      <Grid size="grow">
                        <Typography
                          component="span"
                          variant="body2"
                          fontWeight="bold">
                          {entry.room || '—'}
                        </Typography>
                        <Typography
                          component="span"
                          variant="body2"
                          color="textSecondary">
                          &nbsp;•&nbsp;
                        </Typography>
                        <Typography component="span" variant="body2">
                          {fixtureTypeLabel}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 0.25 }}>
                          {entry.quantity != null && (
                            <Typography variant="caption" color="textSecondary">
                              {entry.quantity} fixtures
                            </Typography>
                          )}
                          {entry.usageRate != null && entry.unit && (
                            <Typography variant="caption" color="textSecondary">
                              {entry.usageRate} {entry.unit}
                            </Typography>
                          )}
                        </Stack>
                      </Grid>
                      <Grid>
                        <ChevronRight
                          sx={(theme) => ({ color: theme.palette.grey[400] })}
                        />
                      </Grid>
                    </Grid>
                    {index < group.entries.length - 1 && <Divider />}
                  </ButtonBase>
                ),
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  ));
